/**
 * canvas-engine/model/assetUrlResolver.js
 *
 * OWN, self-contained "presigned URL at display time" plumbing for the Canvas
 * Engine, shared by the Console builder and the published reader.
 *
 * WHY: image assets are persisted by their DURABLE stored key/URL (never a
 * pre-signed URL, which expires). At RENDER time the durable key is exchanged
 * for a fresh, resolvable (usually pre-signed) URL. This module provides:
 *
 *   - `AssetUrlResolverContext` / `AssetUrlResolverProvider` — each app injects
 *     its OWN resolver `(storedKey: string) => Promise<string> | string`
 *     (admin-console-ui: canvas-builder fileStore `getPreSignedUrl`; academy-ui
 *     reader: `getPreSignedURL`). The engine itself imports NO app API client,
 *     preserving the zero-reuse constraint.
 *   - `usePresignedSrc(rawSrc)` — resolves a display URL for a stored key,
 *     memoized in a process-wide cache so the same key is resolved once and
 *     reused across every image and re-render.
 *   - `EngineImage` — a tiny `<img>` wrapper that runs its `src` through
 *     `usePresignedSrc`.
 *
 * PARITY / SAFETY: when NO provider is mounted (or no resolver is supplied),
 * `usePresignedSrc` returns the raw src unchanged, so existing behavior and all
 * Preview↔Reader parity are preserved by construction. The resolver NEVER
 * mutates the model, so persistence keeps writing the durable stored key.
 *
 * NOTE: authored as `.js` (JSX compiles fine under CRA/babel) to match the
 * engine's other `model/*` modules (`assetRef.js`, `canvasModel.js`) so the
 * published reader resolves the bare specifier `canvas-engine/model/assetUrlResolver`
 * exactly like those proven imports.
 *
 * Pure engine module: React only, no DOM/window assumptions beyond React.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * Context carrying an async resolver from a durable stored key to a fresh
 * display URL. `null` means "no resolver" → render the raw src as-is.
 * @type {React.Context<null | ((storedKey: string) => (Promise<string>|string))>}
 */
export const AssetUrlResolverContext = createContext(null);

/**
 * Process-wide cache of `storedKey -> resolvedDisplayUrl`. Shared across every
 * `EngineImage`/`usePresignedSrc` call so a given key is resolved only once per
 * session (avoids duplicate network calls for repeated/identical assets).
 * @type {Map<string, string>}
 */
const RESOLVED_CACHE = new Map();

/** Trim a value to a string, or '' when not a string. */
function toKey(v) {
  return typeof v === 'string' ? v.trim() : '';
}

/** True for an already-usable absolute http(s) URL (rendered as-is, no resolve). */
function isHttpUrl(v) {
  return /^https?:\/\//i.test(String(v || '').trim());
}

function deriveStoredKeyFromPresignedUrl(value) {
  const raw = toKey(value);
  if (!isHttpUrl(raw)) return '';

  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();
    const isS3Host = host.includes('.s3.') || host.startsWith('s3.') || host.includes('s3.amazonaws.com');
    const hasSignature =
      url.searchParams.has('X-Amz-Signature') ||
      url.searchParams.has('X-Amz-Credential') ||
      url.searchParams.has('AWSAccessKeyId') ||
      url.searchParams.has('Signature');

    if (!isS3Host || !hasSignature) return '';

    const parts = url.pathname
      .split('/')
      .filter(Boolean)
      .map((part) => {
        try {
          return decodeURIComponent(part);
        } catch (_error) {
          return part;
        }
      });

    if (!parts.length) return '';
    if (host.startsWith('s3.') && parts.length > 1) return parts.slice(1).join('/');
    return parts.join('/');
  } catch (_error) {
    return '';
  }
}

function getResolverKey(key) {
  return deriveStoredKeyFromPresignedUrl(key) || key;
}

/**
 * Provide an asset-URL resolver to the engine's image renderers.
 *
 * @param {object} props
 * @param {(storedKey: string) => (Promise<string>|string)} props.resolve
 *   Resolver from a durable stored key/URL to a display URL. Should return the
 *   input unchanged for already-usable absolute URLs.
 * @param {React.ReactNode} props.children
 */
export function AssetUrlResolverProvider({ resolve, children }) {
  const value = typeof resolve === 'function' ? resolve : null;
  return (
    <AssetUrlResolverContext.Provider value={value}>
      {children}
    </AssetUrlResolverContext.Provider>
  );
}

/**
 * Resolve a display URL for a durable stored key using the resolver from
 * context. Returns the raw src synchronously (from cache or as-is) and updates
 * to the resolved URL once available.
 *
 * @param {string} rawSrc durable stored key or already-usable URL
 * @returns {string} the URL to render right now
 */
export function usePresignedSrc(rawSrc) {
  const resolve = useContext(AssetUrlResolverContext);
  const key = toKey(rawSrc);

  const [url, setUrl] = useState(() => {
    if (!key) return '';
    const resolverKey = getResolverKey(key);
    if (RESOLVED_CACHE.has(key)) return RESOLVED_CACHE.get(key);
    if (RESOLVED_CACHE.has(resolverKey)) return RESOLVED_CACHE.get(resolverKey);
    // With a resolver present, a bare stored key is NOT directly loadable — start
    // empty so we don't flash a broken <img> before the pre-signed URL arrives.
    // Absolute URLs (and the no-resolver case) render immediately, unchanged.
    if (typeof resolve === 'function' && (resolverKey !== key || !isHttpUrl(key))) return '';
    return key;
  });

  useEffect(() => {
    // No key, or no resolver mounted → render the raw value unchanged.
    if (!key || typeof resolve !== 'function') {
      setUrl(key);
      return undefined;
    }
    const resolverKey = getResolverKey(key);
    if (isHttpUrl(key) && resolverKey === key) {
      setUrl(key);
      return undefined;
    }
    // Already resolved once this session → reuse immediately.
    if (RESOLVED_CACHE.has(key) || RESOLVED_CACHE.has(resolverKey)) {
      setUrl(RESOLVED_CACHE.get(key) || RESOLVED_CACHE.get(resolverKey));
      return undefined;
    }

    let cancelled = false;
    Promise.resolve()
      .then(() => resolve(resolverKey))
      .then((resolved) => {
        const finalUrl = toKey(resolved) || key; // fall back to the key on empty
        RESOLVED_CACHE.set(key, finalUrl);
        RESOLVED_CACHE.set(resolverKey, finalUrl);
        if (!cancelled) setUrl(finalUrl);
      })
      .catch(() => {
        // Resolution failed (network/expired) → fall back to the raw key so we
        // never render a blank image; do NOT cache the failure so a later
        // render can retry.
        if (!cancelled) setUrl(key);
      });

    return () => {
      cancelled = true;
    };
  }, [key, resolve]);

  return url;
}

/**
 * `<img>` wrapper that resolves its `src` (a durable stored key or URL) to a
 * fresh display URL via `usePresignedSrc`.
 *
 * @param {object} props
 * @param {string} props.src durable stored key or URL
 * @param {string} [props.alt]
 * @param {object} [props.style]
 * @param {string} [props.className]
 */
export function EngineImage({ src, alt, style, className }) {
  const resolved = usePresignedSrc(src);
  return <img className={className} src={resolved} alt={alt == null ? '' : String(alt)} style={style} />;
}

export default EngineImage;
